import React, { useId } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { LayoutGroup, motion } from 'framer-motion';
import { Label } from '@/components/ui/Label';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

const CONTENT_CLASSES: Record<SegmentedControlSize, string> = {
    default: 'gap-1.5',
    compact: 'gap-[5px]',
};

const ICON_SLOT_CLASSES: Record<SegmentedControlSize, string> = {
    default: '[&>svg]:size-3.5',
    compact: '[&>svg]:size-3',
};

const segmentedControlTrackVariants = cva(
    'flex flex-shrink-0 flex-row items-center rounded-[4px] bg-grey-700 p-0.5',
    {
        variants: {
            size: {
                default: 'h-9',
                compact: 'h-7',
            },
        },
        defaultVariants: {
            size: 'default',
        },
    },
);

const segmentedControlOptionVariants = cva(
    'relative flex flex-row items-center justify-center whitespace-nowrap rounded-[2px] px-2 transition-colors',
    {
        variants: {
            size: {
                default: 'h-8 min-w-14 text-sm',
                compact: 'h-6 min-w-8 text-xs',
            },
            selected: {
                true: 'font-medium text-white-100',
                false: 'font-normal text-grey-300 hover:text-white-100',
            },
        },
        defaultVariants: {
            size: 'default',
            selected: false,
        },
    },
);

export type SegmentedControlSize = NonNullable<
    VariantProps<typeof segmentedControlTrackVariants>['size']
>;

interface SegmentedControlOption<T extends string> {
    value: T;
    label: string;
    icon?: React.ReactNode;
    disabled?: boolean;
    disabledReason?: string;
}

interface SegmentedControlProps<T extends string> {
    value: T;
    onValueChange: (value: T) => void;
    options: SegmentedControlOption<T>[];
    isLoading?: boolean;
    label?: string;
    labelClassName?: string;
    required?: boolean;
    size?: SegmentedControlSize;
    fullWidth?: boolean;
}

export function SegmentedControl<T extends string>({
    value,
    onValueChange,
    options,
    isLoading = false,
    label,
    labelClassName,
    required,
    size = 'default',
    fullWidth = false,
}: SegmentedControlProps<T>) {
    const layoutId = useId();
    const groupLabelId = useId();

    return (
        <div
            className={cn(
                'flex min-w-0 flex-col gap-0',
                fullWidth ? 'w-full' : 'w-fit',
            )}
        >
            {label && (
                <Label
                    id={groupLabelId}
                    required={Boolean(required)}
                    className={cn('mb-2 block text-sm-medium', labelClassName)}
                >
                    {label}
                </Label>
            )}
            <LayoutGroup id={layoutId}>
                <div
                    role={label && 'group'}
                    aria-labelledby={label && groupLabelId}
                    className={cn(
                        segmentedControlTrackVariants({ size }),
                        isLoading && 'pointer-events-none',
                    )}
                >
                    {options.map((option) => {
                        const isActive = value === option.value;
                        const button = (
                            <motion.button
                                type="button"
                                onClick={() => {
                                    if (!option.disabled) {
                                        onValueChange(option.value);
                                    }
                                }}
                                disabled={isLoading || Boolean(option.disabled)}
                                className={cn(
                                    segmentedControlOptionVariants({
                                        size,
                                        selected: isActive,
                                    }),
                                    fullWidth && 'flex-1',
                                    isLoading && 'opacity-70',
                                    option.disabled &&
                                        'pointer-events-none cursor-not-allowed opacity-40',
                                )}
                                whileTap={
                                    isLoading || option.disabled
                                        ? {}
                                        : { scale: 0.97 }
                                }
                                transition={{
                                    duration: 0.12,
                                    ease: [0.32, 0.72, 0, 1],
                                }}
                            >
                                {isActive && (
                                    <motion.div
                                        layoutId="activeSegment"
                                        className={`absolute inset-0 bg-grey-500 rounded-[2px] ${isLoading ? 'overflow-hidden' : ''}`}
                                        transition={{
                                            type: 'spring',
                                            stiffness: 350,
                                            damping: 35,
                                            mass: 0.8,
                                        }}
                                    >
                                        {isLoading && (
                                            <motion.div
                                                className="absolute inset-0 bg-gradient-to-r from-transparent via-white-100/10 to-transparent"
                                                animate={{
                                                    x: ['-100%', '100%'],
                                                }}
                                                transition={{
                                                    duration: 1.2,
                                                    repeat: Infinity,
                                                    ease: 'linear',
                                                }}
                                            />
                                        )}
                                    </motion.div>
                                )}
                                <span
                                    className={cn(
                                        'relative z-10 flex items-center',
                                        CONTENT_CLASSES[size],
                                    )}
                                >
                                    {option.icon && (
                                        <span
                                            className={cn(
                                                'flex flex-shrink-0 items-center',
                                                ICON_SLOT_CLASSES[size],
                                            )}
                                        >
                                            {option.icon}
                                        </span>
                                    )}
                                    {option.label}
                                </span>
                            </motion.button>
                        );

                        if (!option.disabled || !option.disabledReason) {
                            return (
                                <React.Fragment key={option.value}>
                                    {button}
                                </React.Fragment>
                            );
                        }

                        return (
                            <Tooltip key={option.value}>
                                <TooltipTrigger asChild>
                                    <span className="inline-flex cursor-not-allowed">
                                        {button}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent
                                    variant="panel"
                                    className="max-w-80"
                                >
                                    {option.disabledReason}
                                </TooltipContent>
                            </Tooltip>
                        );
                    })}
                </div>
            </LayoutGroup>
        </div>
    );
}
